import {
  Component,
  type ElementRef,
  computed,
  contentChildren,
  forwardRef,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { coerceBoolean } from '../../cdk/utils';
import type { DropdownAnchorDirective } from './dropdown-anchor.directive';
import { DropdownItemDirective } from './dropdown-item.directive';
import { DropdownNavigatorDirective } from './dropdown-navigator.directive';
import { DropdownPositionDirective } from './dropdown-position.directive';
import { DropdownStateDirective } from './dropdown-state.directive';

@Component({
  selector: 'tvd-dropdown',
  template: `
    <dialog
      class="focus:outline-0 backdrop:bg-transparent border-0 rounded-xl m-0 p-0 overflow-y-auto bg-card shadow-xl overflow-x-hidden text-ellipsis"
      #container
      (click)="$event.target === container && close($event)"
    >
      <ng-content />
    </dialog>
  `,
  styles: [
    `
      dialog {
        transition:
          transform 0.18s ease,
          opacity 0.18s ease,
          display 0.18s allow-discrete,
          overlay 0.18s allow-discrete;
        opacity: 0;
      }
      dialog[data-placement='below'] {
        transform: translateY(-8px);
      }
      dialog[data-placement='above'] {
        transform: translateY(8px);
      }
      dialog[open] {
        transform: translateY(0);
        opacity: 1;
      }

      @starting-style {
        dialog[open][data-placement='below'] {
          transform: translateY(-8px);
          opacity: 0;
        }
        dialog[open][data-placement='above'] {
          transform: translateY(8px);
          opacity: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        dialog {
          transition: none;
          transform: none !important;
          opacity: 1 !important;
        }
      }
    `,
  ],
  host: {
    class: 'block z-10 fixed top-0 left-0',
  },
  hostDirectives: [
    DropdownPositionDirective,
    DropdownNavigatorDirective,
    DropdownStateDirective,
  ],
})
export class DropdownComponent {
  readonly anchor = input<DropdownAnchorDirective>();
  readonly attachTo = model<HTMLElement>();
  /** When aligned the width of the dropdown will always match the width of anchor*/
  readonly alignWithAnchor = input(false, { transform: coerceBoolean });
  readonly closeOnSelect = input(true, { transform: coerceBoolean });
  readonly disabled = input(false, { transform: coerceBoolean });
  readonly hasBackdrop = input(true, { transform: coerceBoolean });
  readonly customClasses = input('', { alias: 'class' });
  readonly closing = output<void>();

  readonly ref = viewChild.required<ElementRef<HTMLDialogElement>>('container');
  readonly container = computed(() => this.ref().nativeElement);

  readonly items = contentChildren(
    forwardRef(() => DropdownItemDirective),
    { read: DropdownItemDirective },
  );

  get isOpen() {
    return this.opened();
  }

  readonly opened = signal(false);

  open() {
    this.opened.set(true);
    // Set initial placement before opening so @starting-style matches on first paint
    try {
      const container = this.container();
      const anchorEl = this.attachTo() || this.anchor()?.nativeElement;
      let placement: 'below' | 'above' = 'below';
      if (anchorEl && typeof window !== 'undefined') {
        const anchorBox = anchorEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight || 0;
        placement = anchorBox.top < viewportHeight / 2 ? 'below' : 'above';
      }
      container.setAttribute('data-placement', placement);
    } catch {}
    if (this.hasBackdrop()) {
      this.container().showModal();
    } else {
      this.container().show();
    }
    this.items()
      .find((item) => item.highlighted())
      ?.scrollIntoView();
  }

  close(event?: MouseEvent) {
    this.opened.set(false);
    this.closing.emit();
    this.container().close();
    for (const item of this.items()) {
      item.highlighted.set(false);
    }
    event?.preventDefault();
    event?.stopPropagation();
  }

  toggle() {
    this.container().open ? this.close() : this.open();
  }
}
